import { useState } from "react";
import { MapPin, MessageCircle, Clock } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import { SERVICE_OPTIONS, ADDRESS, PHONE_DISPLAY, ALT_PHONE_DISPLAY, WHATSAPP_LINK } from "@/data/site";

export default function Contact() {
  const [tab, setTab] = useState("general");
  const active = SERVICE_OPTIONS.find((o) => o.value === tab);

  return (
    <main className="pt-[72px]" data-testid="contact-page">
      <section className="grid-lines relative overflow-hidden border-b border-white/10">
        <div className="pointer-events-none absolute -top-32 left-1/3 h-[400px] w-[600px] rounded-full bg-[#D9A92E]/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-28">
          <FadeUp>
            <p className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766] sm:text-sm">
              Contact Spark Plug
            </p>
            <h1 className="font-display mt-4 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Let's build your <span className="text-[#D9A92E]">next win.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#A1A1AA]">
              Connect with our engineers and growth experts. Dedicated forms for every service —
              or skip the form entirely and WhatsApp us directly.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-[380px_1fr]">
          <FadeUp className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-[#131316] p-7">
              <MapPin className="h-6 w-6 text-[#D9A92E]" />
              <h3 className="mt-4 text-base font-semibold text-white">Our office</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]" data-testid="contact-page-address">
                {ADDRESS}
              </p>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(ADDRESS)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-page-directions-link"
                className="mt-3 inline-block text-sm font-semibold text-[#E9C766] transition-colors hover:text-[#D9A92E]"
              >
                Get directions →
              </a>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#131316] p-7">
              <MessageCircle className="h-6 w-6 text-[#D9A92E]" />
              <h3 className="mt-4 text-base font-semibold text-white">Fastest response</h3>
              <p className="mt-2 text-sm text-[#A1A1AA]">{PHONE_DISPLAY}</p>
              <p className="mt-1 text-sm text-[#A1A1AA]">{ALT_PHONE_DISPLAY}</p>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-page-whatsapp-button"
                className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white transition-all duration-300 hover:brightness-110"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Us Now
              </a>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#131316] p-7">
              <Clock className="h-6 w-6 text-[#D9A92E]" />
              <h3 className="mt-4 text-base font-semibold text-white">Hours</h3>
              <p className="mt-2 text-sm text-[#A1A1AA]">Mon – Sat, 11:00 AM – 5:00 PM ET</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-[#131316] p-8 lg:p-10">
              <div className="flex flex-wrap gap-2" role="tablist" data-testid="contact-service-tabs">
                {SERVICE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    role="tab"
                    aria-selected={tab === o.value}
                    onClick={() => setTab(o.value)}
                    data-testid={`contact-tab-${o.value}`}
                    className={`mono rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                      tab === o.value
                        ? "bg-[#D9A92E] text-[#09090B] shadow-[0_0_20px_rgba(217,169,46,0.4)]"
                        : "border border-white/10 text-[#A1A1AA] hover:border-[#D9A92E]/40 hover:text-white"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <h2 className="font-display mt-8 text-xl font-bold text-white sm:text-2xl">
                {active.label}
              </h2>
              <p className="mt-2 text-sm text-[#A1A1AA]">
                Fill this in and the right specialist gets it — no inbox ping-pong.
              </p>
              <div className="mt-7">
                <InquiryForm
                  key={tab}
                  service={tab}
                  testidPrefix={`inquiry-form-${tab}`}
                  submitLabel={`Send ${active.label}`}
                />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
